// Support page content, transcribed from Jessica's reference graphic.
// Text only. Ticket/search references removed (chat + email only).
// Rows with verify: true make claims about app behavior and must be
// confirmed against the product before shipping.

export const SUPPORT_EMAIL = "support@zynacular.com";

export const supportSections = [
  {
    id: "account-login",
    title: "Account & Login",
    description: "Access your account and manage your Zynacular experience.",
    rows: [
      {
        issue: "I cannot sign in.",
        solution:
          "Use the \"Forgot Password\" link or check your email. Still stuck? Contact support.",
      },
      {
        issue: "I didn't receive my password reset email.",
        solution:
          "Check your spam or junk folder and add support@zynacular.com to your contacts.",
      },
      {
        issue: "My account is not recognized.",
        solution:
          "Make sure you're using the same email you used when you created your account.",
      },
      {
        issue: "I am locked out.",
        solution:
          "Wait a few minutes and try again. If the issue continues, contact support.",
      },
    ],
  },
  {
    id: "application-setup",
    title: "Application & Setup",
    description: "Create your application with confidence.",
    rows: [
      {
        issue: "I'm not sure how to start an application.",
        solution:
          "Click \"New Application\" and follow the step-by-step prompts.",
      },
      {
        issue: "I'm not sure what information to enter.",
        solution:
          "Include your experience, skills, and the job description (if you have one). The more information you provide, the stronger your documents.",
      },
      {
        issue: "I entered something incorrect.",
        solution:
          "You can go back and edit your application at any time before generating your documents.",
        verify: true,
      },
      {
        issue: "I used the wrong job description.",
        solution:
          "You'll need to start a new application with the correct job description.",
        verify: true,
      },
    ],
  },
  {
    id: "billing-credits",
    title: "Billing & Credits",
    description: "Manage your purchases and credits.",
    rows: [
      {
        issue: "My credit did not appear.",
        solution:
          "Refresh your account and verify your purchase. If it still doesn't appear, contact support with your purchase details.",
      },
      {
        issue: "I was charged incorrectly or twice.",
        solution:
          "Do not make another purchase. Contact support with the date, amount, and charge information so we can review it.",
      },
      {
        issue: "A credit was used, but my documents were not created.",
        solution:
          "Credits are only used for documents you selected. If documents were not generated, check \"My Documents\" or contact support.",
        verify: true,
      },
    ],
  },
  {
    id: "document-generation",
    title: "Document Generation",
    description: "Get help with your selected documents.",
    rows: [
      {
        issue: "My document did not generate.",
        solution:
          "Confirm you selected the document(s) you want. Only selected documents are generated. If it's still missing, contact support.",
        verify: true,
      },
      {
        issue: "Only some of my documents are showing.",
        solution:
          "This is normal. Zynacular only generates the documents you selected for that application.",
      },
      {
        issue: "The information in my documents is wrong.",
        solution:
          "Compare your documents with the information in your application. If you need changes, start a new application with the correct information.",
      },
      {
        issue: "I want to change something in my documents.",
        solution:
          "Minor edits can be made after download. For major changes, update your application and generate a new set of documents.",
        verify: true,
      },
    ],
  },
  {
    id: "document-review",
    title: "Document Review",
    description: "Formatting, content and document-specific questions.",
    rows: [
      {
        issue: "Why does the formatting look different from my old resume?",
        solution:
          "Zynacular optimizes your documents for ATS and recruiter readability, which may look different from previous versions.",
      },
      {
        issue: "Should I use a Resume or CV?",
        solution:
          "Use the document type that best fits your industry and target role.",
      },
      {
        issue: "Do I need to update LinkedIn every time I apply?",
        solution:
          "No. Your LinkedIn Profile is generated for the specific job you selected. Update it only when you want to reflect a new role or opportunity.",
      },
    ],
  },
  {
    id: "technical-issues",
    title: "Technical Issues",
    description: "Resolve errors and performance issues.",
    rows: [
      {
        issue: "I am getting an error message.",
        solution:
          "Take a screenshot of the error and try again. If it continues, contact support with the screenshot and a short description of what you were doing.",
      },
      {
        issue: "A page will not load.",
        solution:
          "Refresh the page, clear your browser cache, or try a different browser. If it still won't load, contact support.",
      },
      {
        issue: "My information is not saving.",
        solution:
          "Make sure all required fields are completed and you have an active internet connection. If the issue continues, try a different device or browser.",
      },
    ],
  },
  {
    id: "faq",
    title: "Frequently Asked Questions",
    description: "Quick answers about Zynacular.",
    rows: [
      {
        issue: "What is Zynacular?",
        solution:
          "Zynacular uses your application to create the professional documents you select, including Resume, CV, Cover Letter, LinkedIn Profile, Professional Bio and Portfolio.",
        verify: true, // confirm Portfolio (and full list) matches the product
      },
      {
        issue: "How does Zynacular work?",
        solution:
          "You provide your information, select the documents you need, and Zynacular customizes each document for your target opportunity.",
      },
      {
        issue: "What does ATS optimized mean?",
        solution:
          "Your documents are structured with relevant keywords and formatting to perform well in Applicant Tracking Systems (ATS).",
      },
      {
        issue: "How quickly are my documents created?",
        solution:
          "Your selected documents are generated together in just a few minutes.",
        verify: true,
      },
    ],
  },
  {
    id: "other",
    title: "Other",
    description: "Additional questions and support.",
    rows: [
      {
        issue: "Something is not working the way I expected.",
        solution:
          "Tell us what you were trying to do, what happened, and where it occurred. Include a screenshot if possible.",
      },
      {
        issue: "Can I use Zynacular for any industry or role?",
        solution:
          "Yes. Zynacular works across industries, roles and experience levels. You can target any job description, company, industry, service, role or other.",
      },
      {
        // Adapted: original referenced ticket submission + 24-hour response
        issue: "I have a question not listed here.",
        solution:
          "Our support team is here to help. Start a chat or email us at support@zynacular.com.",
        verify: true,
      },
      {
        // Adapted: original referenced tickets
        issue: "I still need help.",
        solution: "Start a chat or email us. We're just a click away.",
      },
    ],
  },
];

// "Not sure what to put in your resume?" callout (rendered by ResumeFormCallout).
export const resumeFormSection = {
  title: "Not sure what to put in your resume?",
  intro:
    "Download our Resume Information Form, fill it in at your own pace, then paste it or upload it in Step 2 (Your Resume) of the Zynacular builder. We'll turn your answers into clear, professional wording.",
  linkText: "Download the Resume Information Form",
  linkNote:
    "Don't have Word? You can open it in Google Docs, Apple Pages, or any app that opens Word files.",
  href: "/downloads/Zynacular_Resume_Information_Form.docx",
  gatherTitle: "What to gather:",
  gather: [
    "Your contact details and the job you want",
    "Each job: employer, title, dates, what you did and what resulted",
    "Numbers you remember, like team size, customers served or time saved (estimates are fine if labeled)",
    "Education, certifications and software you've used",
  ],
  notes: [
    "No job history? Volunteer work, internships, school projects and gig work all count.",
    "We'll polish your wording, but we never invent anything. Only share what's true.",
  ],
};
